import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "../layouts/DashboardLayout";
import api from "../api";
import { toast } from "react-toastify";

const CONTENT_TYPES = [
  { value: "definition", label: "Definition", color: "#6366f1" },
  { value: "positive_example", label: "Positive", color: "#22c55e" },
  { value: "negative_example", label: "Negative", color: "#ef4444" },
  { value: "boundary_rule", label: "Boundary Rule", color: "#f59e0b" },
];

const EMPTY_FORM = {
  signal_id: "",
  content_type: "positive_example",
  content_text: "",
  explanation: "",
  source: "expert_authored",
};

export default function SignalKnowledgeBase() {
  const [entries, setEntries] = useState([]);
  const [signals, setSignals] = useState([]);
  const [tier1Signals, setTier1Signals] = useState([]);
  const [aliasMap, setAliasMap] = useState({}); // alias -> full descriptive name, display-only
  const [loading, setLoading] = useState(true);
  const [filterSignal, setFilterSignal] = useState("");
  const [filterType, setFilterType] = useState("");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Validate panel state
  const [validateSignal, setValidateSignal] = useState("");
  const [validateText, setValidateText] = useState("");
  const [validateResult, setValidateResult] = useState(null);
  const [validating, setValidating] = useState(false);

  // Embed state
  const [embedding, setEmbedding] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterSignal) params.signal_id = filterSignal;
      if (filterType) params.content_type = filterType;
      const [entriesRes, signalsRes] = await Promise.all([
        api.getSignalKBEntries(params),
        api.getSignalKBSignals(),
      ]);
      setEntries(entriesRes.data.entries || []);
      setSignals(signalsRes.data.signals || []);
      setTier1Signals(signalsRes.data.tier1 || []);
      setAliasMap(signalsRes.data.alias_map || {});
    } catch (err) {
      toast.error("Failed to load knowledge base");
    } finally {
      setLoading(false);
    }
  }, [filterSignal, filterType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Display "Full Name (ALIAS)" everywhere, but every <option>/value below is
  // still the bare alias — that's what actually gets submitted to the server.
  const displayLabel = (alias) => (aliasMap[alias] ? `${aliasMap[alias]} (${alias})` : alias);

  const openAdd = () => {
    setEditingEntry(null);
    setForm({ ...EMPTY_FORM, signal_id: filterSignal || "" });
    setShowModal(true);
  };

  const openEdit = (entry) => {
    setEditingEntry(entry);
    setForm({
      signal_id: entry.signal_id,
      content_type: entry.content_type,
      content_text: entry.content_text,
      explanation: entry.explanation || "",
      source: entry.source,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.signal_id.trim() || !form.content_text.trim()) {
      toast.error("Signal ID and content text are required");
      return;
    }
    setSaving(true);
    try {
      if (editingEntry) {
        await api.updateSignalKBEntry(editingEntry.id, {
          content_type: form.content_type,
          content_text: form.content_text.trim(),
          explanation: form.explanation.trim() || null,
        });
        toast.success("Entry updated");
      } else {
        await api.createSignalKBEntry({
          signal_id: form.signal_id.trim(),
          content_type: form.content_type,
          content_text: form.content_text.trim(),
          explanation: form.explanation.trim() || null,
          source: form.source,
        });
        toast.success("Entry created");
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete this ${entry.content_type} entry for ${entry.signal_id}?`)) return;
    try {
      await api.deleteSignalKBEntry(entry.id);
      toast.success("Entry deleted");
      fetchData();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleValidate = async () => {
    if (!validateSignal || !validateText.trim()) {
      toast.error("Signal and evidence text required");
      return;
    }
    setValidating(true);
    setValidateResult(null);
    try {
      const res = await api.validateSignalKBEvidence({
        signal_id: validateSignal,
        evidence_text: validateText.trim(),
      });
      setValidateResult(res.data);
    } catch {
      toast.error("Validation failed");
    } finally {
      setValidating(false);
    }
  };

  // Group entries by signal for summary counts
  const groupedCounts = entries.reduce((acc, e) => {
    if (!acc[e.signal_id]) acc[e.signal_id] = { positive_example: 0, negative_example: 0, definition: 0, boundary_rule: 0 };
    acc[e.signal_id][e.content_type] = (acc[e.signal_id][e.content_type] || 0) + 1;
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
          <div>
            <h1 style={{ fontSize: "22px", fontWeight: 700, margin: 0 }}>Signal Knowledge Base</h1>
            <p style={{ margin: "4px 0 0", color: "var(--text-secondary, #6b7280)", fontSize: "14px" }}>
              Define evidence rules for signal extraction validation ({entries.length} entries across {Object.keys(groupedCounts).length} signals)
            </p>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={async () => {
                setEmbedding(true);
                try {
                  const res = await api.embedAllSignalKB();
                  toast.success(res.data.message);
                  fetchData();
                } catch {
                  toast.error("Embedding generation failed");
                } finally {
                  setEmbedding(false);
                }
              }}
              disabled={embedding}
              style={btnStyle("#0ea5e9")}
            >
              {embedding ? "Generating…" : "⚡ Generate Embeddings"}
            </button>
            <button onClick={openAdd} style={btnStyle("#6366f1")}>+ Add Entry</button>
          </div>
        </div>

        {/* Validate Panel */}
        <div style={cardStyle}>
          <h3 style={{ margin: "0 0 12px", fontSize: "15px", fontWeight: 600 }}>Test Evidence Validation</h3>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ flex: "0 0 220px" }}>
              <label style={labelStyle}>Signal</label>
              <select value={validateSignal} onChange={e => setValidateSignal(e.target.value)} style={inputStyle}>
                <option value="">— select signal —</option>
                {tier1Signals.map(s => (
                  <option key={s} value={s}>{displayLabel(s)}</option>
                ))}
                {signals.filter(s => !tier1Signals.includes(s)).map(s => (
                  <option key={s} value={s}>{displayLabel(s)}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: "1 1 300px" }}>
              <label style={labelStyle}>Evidence text</label>
              <input
                type="text"
                value={validateText}
                onChange={e => setValidateText(e.target.value)}
                placeholder="e.g. I want to end it all"
                style={inputStyle}
                onKeyDown={e => e.key === "Enter" && handleValidate()}
              />
            </div>
            <button onClick={handleValidate} disabled={validating} style={btnStyle("#0ea5e9")}>
              {validating ? "Validating…" : "Validate"}
            </button>
          </div>
          {validateResult && <ValidateResultBadge result={validateResult} />}
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: "10px", marginBottom: "16px", flexWrap: "wrap" }}>
          <div>
            <label style={labelStyle}>Filter by signal</label>
            <select value={filterSignal} onChange={e => setFilterSignal(e.target.value)} style={{ ...inputStyle, width: "220px" }}>
              <option value="">All signals</option>
              {tier1Signals.map(s => (
                <option key={s} value={s}>{displayLabel(s)}</option>
              ))}
              {signals.filter(s => !tier1Signals.includes(s)).map(s => (
                <option key={s} value={s}>{displayLabel(s)}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Filter by type</label>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ ...inputStyle, width: "180px" }}>
              <option value="">All types</option>
              {CONTENT_TYPES.map(ct => (
                <option key={ct.value} value={ct.value}>{ct.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "48px", color: "#6b7280" }}>Loading…</div>
        ) : entries.length === 0 ? (
          <div style={{ ...cardStyle, textAlign: "center", padding: "48px", color: "#6b7280" }}>
            No entries yet. Click <strong>+ Add Entry</strong> to create the first one.
          </div>
        ) : (
          <div style={cardStyle}>
            <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: "900px", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border, #e5e7eb)" }}>
                  {["Signal", "Type", "Content", "Explanation", "Embedding", "Source", ""].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map(entry => (
                  <tr key={entry.id} style={{ borderBottom: "1px solid var(--border-light, #f3f4f6)" }}>
                    <td style={tdStyle}>
                      <span style={{
                        fontFamily: "monospace", fontSize: "12px",
                        background: "#f3f4f6",
                        padding: "2px 6px", borderRadius: "4px"
                      }}>
                        {displayLabel(entry.signal_id)}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <TypeBadge type={entry.content_type} />
                    </td>
                    <td style={{ ...tdStyle, maxWidth: "280px" }}>
                      <span title={entry.content_text} style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {entry.content_text}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, maxWidth: "200px", color: "#6b7280" }}>
                      <span title={entry.explanation || ""} style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {entry.explanation || "—"}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: "11px", color: entry.has_embedding ? "#22c55e" : "#9ca3af" }}>
                        {entry.has_embedding ? "✓ ready" : "pending"}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, fontSize: "11px", color: "#9ca3af" }}>{entry.source}</td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      <button onClick={() => openEdit(entry)} style={actionBtn("#6366f1")}>Edit</button>
                      <button onClick={() => handleDelete(entry)} style={actionBtn("#ef4444")}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div style={overlayStyle} onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={modalStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700 }}>
                {editingEntry ? "Edit Entry" : "Add Knowledge Base Entry"}
              </h2>
              <button onClick={() => setShowModal(false)} style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#6b7280" }}>×</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {!editingEntry && (
                <div>
                  <label style={labelStyle}>Signal ID *</label>
                  <input
                    value={form.signal_id}
                    onChange={e => setForm(f => ({ ...f, signal_id: e.target.value }))}
                    placeholder="e.g. suicidal_ideation_present"
                    style={inputStyle}
                    list="signal-datalist"
                  />
                  <datalist id="signal-datalist">
                    {tier1Signals.map(s => <option key={s} value={s} label={displayLabel(s)} />)}
                    {signals.filter(s => !tier1Signals.includes(s)).map(s => <option key={s} value={s} label={displayLabel(s)} />)}
                  </datalist>
                </div>
              )}

              <div>
                <label style={labelStyle}>Content Type *</label>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {CONTENT_TYPES.map(ct => (
                    <label key={ct.value} style={{
                      display: "flex", alignItems: "center", gap: "6px", cursor: "pointer",
                      padding: "6px 12px", borderRadius: "6px", border: "1px solid",
                      borderColor: form.content_type === ct.value ? ct.color : "#e5e7eb",
                      background: form.content_type === ct.value ? ct.color + "15" : "transparent",
                      fontSize: "13px", fontWeight: form.content_type === ct.value ? 600 : 400,
                    }}>
                      <input type="radio" name="content_type" value={ct.value}
                        checked={form.content_type === ct.value}
                        onChange={() => setForm(f => ({ ...f, content_type: ct.value }))}
                        style={{ display: "none" }}
                      />
                      <span style={{ color: ct.color }}>●</span> {ct.label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label style={labelStyle}>Content Text * <span style={{ color: "#9ca3af", fontWeight: 400 }}>(the actual example text or rule)</span></label>
                <textarea
                  value={form.content_text}
                  onChange={e => setForm(f => ({ ...f, content_text: e.target.value }))}
                  rows={3}
                  placeholder={
                    form.content_type === "positive_example"
                      ? "e.g. I want to die / I wish I wouldn't wake up"
                      : form.content_type === "negative_example"
                      ? "e.g. I'm really tired of all this (fatigue, not suicidal ideation)"
                      : form.content_type === "boundary_rule"
                      ? "e.g. CM14 requires explicit confirmation of feeling as valid — mirroring alone is CM13"
                      : "Full definition of the signal"
                  }
                  style={{ ...inputStyle, resize: "vertical" }}
                />
              </div>

              <div>
                <label style={labelStyle}>Explanation <span style={{ color: "#9ca3af", fontWeight: 400 }}>(why this counts / doesn't count)</span></label>
                <textarea
                  value={form.explanation}
                  onChange={e => setForm(f => ({ ...f, explanation: e.target.value }))}
                  rows={2}
                  placeholder="Optional reasoning to help future validation…"
                  style={{ ...inputStyle, resize: "vertical" }}
                />
              </div>

              {!editingEntry && (
                <div>
                  <label style={labelStyle}>Source</label>
                  <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} style={inputStyle}>
                    <option value="expert_authored">Expert Authored</option>
                    <option value="mitl_derived">MITL Derived</option>
                  </select>
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "24px" }}>
              <button onClick={() => setShowModal(false)} style={btnStyle("#6b7280")}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={btnStyle("#6366f1")}>
                {saving ? "Saving…" : editingEntry ? "Save Changes" : "Create Entry"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function TypeBadge({ type }) {
  const ct = CONTENT_TYPES.find(c => c.value === type);
  if (!ct) return <span>{type}</span>;
  return (
    <span style={{
      fontSize: "11px", fontWeight: 600, padding: "2px 8px", borderRadius: "10px",
      background: ct.color + "20", color: ct.color, whiteSpace: "nowrap",
    }}>
      {ct.label}
    </span>
  );
}

function ValidateResultBadge({ result }) {
  const colors = { VALID: "#22c55e", SUSPICIOUS: "#f59e0b", UNKNOWN: "#9ca3af" };
  const icons = { VALID: "✓", SUSPICIOUS: "⚠", UNKNOWN: "?" };
  const color = colors[result.verdict] || "#9ca3af";
  return (
    <div style={{
      marginTop: "12px", padding: "12px 16px", borderRadius: "8px",
      background: color + "10", border: `1px solid ${color}40`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
        <span style={{ fontSize: "20px", color }}>{icons[result.verdict]}</span>
        <strong style={{ color, fontSize: "15px" }}>{result.verdict}</strong>
        <span style={{ fontSize: "12px", color: "#6b7280" }}>
          confidence: {(result.confidence * 100).toFixed(0)}% · method: {result.method}
        </span>
      </div>
      {result.matched_entry && (
        <div style={{ fontSize: "12px", color: "#6b7280" }}>
          <TypeBadge type={result.matched_entry.content_type} />
          {" "}matched: <em>"{result.matched_entry.content_text?.slice(0, 120)}"</em>
        </div>
      )}
      {result.verdict === "UNKNOWN" && (
        <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "4px" }}>
          No KB entries found for this signal yet. Add some entries to enable validation.
        </div>
      )}
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const cardStyle = {
  background: "var(--card-bg, #fff)",
  border: "1px solid var(--border, #e5e7eb)",
  borderRadius: "10px",
  padding: "20px",
  marginBottom: "16px",
};

const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: "6px",
  border: "1px solid var(--border, #d1d5db)",
  fontSize: "13px",
  background: "var(--input-bg, #fff)",
  color: "var(--text, #111827)",
  boxSizing: "border-box",
};

const labelStyle = {
  display: "block",
  fontSize: "12px",
  fontWeight: 600,
  color: "var(--text-secondary, #374151)",
  marginBottom: "4px",
};

const thStyle = {
  textAlign: "left",
  padding: "8px 12px",
  fontSize: "11px",
  fontWeight: 700,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const tdStyle = {
  padding: "10px 12px",
  verticalAlign: "middle",
  fontSize: "13px",
};

const overlayStyle = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
  display: "flex", alignItems: "center", justifyContent: "center",
  zIndex: 1000,
};

const modalStyle = {
  background: "var(--card-bg, #fff)",
  borderRadius: "12px",
  padding: "28px",
  width: "min(600px, 95vw)",
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
};

function btnStyle(color) {
  return {
    padding: "8px 16px", borderRadius: "6px", border: "none", cursor: "pointer",
    background: color, color: "#fff", fontSize: "13px", fontWeight: 600,
    opacity: 1, transition: "opacity 0.15s",
  };
}

function actionBtn(color) {
  return {
    padding: "3px 10px", marginRight: "4px", borderRadius: "4px", border: "none",
    cursor: "pointer", background: color + "15", color, fontSize: "12px", fontWeight: 600,
  };
}
